import { supabase } from '@/integrations/supabase/client';

/**
 * Utility functions for downloading files from Supabase storage
 */

// Helper function to trigger file download
const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

// Helper function to get file extension from mime type
const getFileExtension = (mimeType: string): string => {
  const mimeToExt: Record<string, string> = {
    'application/pdf': '.pdf',
    'image/jpeg': '.jpg',
    'image/jpg': '.jpg',
    'image/png': '.png',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx'
  };
  return mimeToExt[mimeType] || '';
};

/**
 * Download a file from Supabase storage by file path
 */
export const downloadFileByPath = async (filePath: string, customFilename?: string) => {
  try {
    const { data, error } = await supabase.storage
      .from('uploads')
      .download(filePath);

    if (error) {
      throw error;
    }

    if (!data) {
      throw new Error('No file data received');
    }

    // Use custom filename or extract from path
    const filename = customFilename || filePath.split('/').pop() || 'download';
    
    triggerDownload(data, filename);
    
    return {
      success: true,
      message: `File "${filename}" downloaded successfully!`
    };

  } catch (error) {
    console.error('Download error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown download error'
    };
  }
};

/**
 * Download a file by upload record ID from the uploads table
 */
export const downloadFileById = async (uploadId: string) => {
  try {
    // First, get the file metadata from the uploads table
    const { data: uploadRecord, error: dbError } = await supabase
      .from('uploads')
      .select('file_path, doc_type, mime_type, size_bytes')
      .eq('id', uploadId)
      .single();

    if (dbError) {
      throw dbError;
    }

    if (!uploadRecord) {
      throw new Error('Upload record not found');
    }

    // Download the file from storage
    const { data: fileData, error: storageError } = await supabase.storage
      .from('uploads')
      .download(uploadRecord.file_path);

    if (storageError) {
      throw storageError;
    }

    if (!fileData) {
      throw new Error('No file data received');
    }

    // Generate filename with proper extension
    const extension = getFileExtension(uploadRecord.mime_type || '');
    const baseFilename = uploadRecord.doc_type || 'document';
    const filename = `${baseFilename}_${uploadId.slice(0, 8)}${extension}`;
    
    triggerDownload(fileData, filename);
    
    return {
      success: true,
      message: `File "${filename}" downloaded successfully!`,
      metadata: uploadRecord
    };

  } catch (error) {
    console.error('Download error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown download error'
    };
  }
};

/**
 * Get all uploaded files for a specific submission
 */
export const getSubmissionFiles = async (submissionId: string) => {
  try {
    const { data, error } = await supabase
      .from('uploads')
      .select('*')
      .eq('submission_id', submissionId)
      .order('uploaded_at', { ascending: false });

    if (error) {
      throw error;
    }

    return {
      success: true,
      data: data || []
    };

  } catch (error) {
    console.error('Error fetching submission files:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Download all files for a specific submission as a ZIP
 * Note: This requires additional setup for ZIP creation
 */
export const downloadSubmissionFilesAsZip = async (submissionId: string) => {
  try {
    // Get all files for the submission
    const filesResult = await getSubmissionFiles(submissionId);
    
    if (!filesResult.success || !filesResult.data) {
      throw new Error(filesResult.error || 'No files found');
    }

    const files = filesResult.data;
    
    if (files.length === 0) {
      throw new Error('No files found for this submission');
    }

    // For now, download files individually
    // TODO: Implement ZIP creation using JSZip library
    const downloadPromises = files.map(file => 
      downloadFileById(file.id)
    );

    const results = await Promise.all(downloadPromises);
    const successCount = results.filter(r => r.success).length;
    
    return {
      success: true,
      message: `Downloaded ${successCount} of ${files.length} files`
    };

  } catch (error) {
    console.error('Bulk download error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Get a signed URL for a file (for preview or external access)
 */
export const getFileSignedUrl = async (filePath: string, expiresIn: number = 3600) => {
  try {
    const { data, error } = await supabase.storage
      .from('uploads')
      .createSignedUrl(filePath, expiresIn);

    if (error) {
      throw error;
    }

    return {
      success: true,
      signedUrl: data.signedUrl,
      expiresAt: new Date(Date.now() + expiresIn * 1000)
    };

  } catch (error) {
    console.error('Signed URL error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Check if a file exists in storage
 */
export const checkFileExists = async (filePath: string) => {
  try {
    const { data, error } = await supabase.storage
      .from('uploads')
      .list(filePath.split('/').slice(0, -1).join('/'), {
        search: filePath.split('/').pop()
      });

    if (error) {
      throw error;
    }

    const fileExists = data && data.length > 0;
    
    return {
      success: true,
      exists: fileExists,
      fileInfo: fileExists ? data[0] : null
    };

  } catch (error) {
    console.error('File check error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};

/**
 * Get file info without downloading
 */
export const getFileInfo = async (uploadId: string) => {
  try {
    const { data, error } = await supabase
      .from('uploads')
      .select('*')
      .eq('id', uploadId)
      .single();

    if (error) {
      throw error;
    }

    // Check if file exists in storage
    const existsResult = await checkFileExists(data.file_path);
    
    return {
      success: true,
      data: {
        ...data,
        exists_in_storage: existsResult.exists,
        storage_info: existsResult.fileInfo
      }
    };

  } catch (error) {
    console.error('File info error:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error'
    };
  }
};
